import { 
// Error handling
ApiError, } from '@common/clientServerApi';
export class Api {
    constructor(apiUrl, authToken) {
        this.apiUrl = apiUrl;
        this.baseUrl = apiUrl;
        this.token = authToken;
        this.authToken = authToken;
    }
    // Update token when refreshed
    updateToken(newToken) {
        this.token = newToken;
        this.authToken = newToken;
    }
    // Generic request method
    async request(path, method = 'GET', body) {
        return makeRequest(this.apiUrl, this.authToken, path, method, body);
    }
    // User authentication
    async loginUser(include, role, subscribeNotifications, tabId) {
        const body = { include, role, subscribeNotifications, tabId };
        return this.request('/login', 'POST', body);
    }
    // Unified Settings
    async getSettings() {
        return this.request('/settings', 'GET');
    }
    async getRecruiterMcpStatus() {
        return this.request('/recruiter/mcp/status', 'GET');
    }
    async connectRecruiterMcp(input) {
        return this.request('/recruiter/mcp/connect', 'POST', input);
    }
    async revokeRecruiterMcp(grantId) {
        return this.request('/recruiter/mcp/revoke', 'POST', { grantId });
    }
    async approveRecruiterMcp(input) {
        return this.request('/recruiter/mcp/approve', 'POST', input);
    }
    async createRecruiterMcpRun(input) {
        return this.request('/recruiter/mcp/runs', 'POST', input);
    }
    async stopRecruiterMcpRun(runId) {
        return this.request('/recruiter/mcp/stop', 'POST', { runId });
    }
    async updateSettings(settings) {
        return this.request('/settings', 'PUT', settings);
    }
    // ── Activity feed (durable inbox) ──────────────────────────────────────
    /**
     * Mark inbox items read. Omit `ids` to mark ALL unread items read.
     * `read` defaults to true.
     */
    async markActivityRead(ids, read = true) {
        const body = ids ? { ids, read } : { read };
        return this.request('/activity/read', 'POST', body);
    }
    /** Dispatch an activity CTA command (Start screening / Reject / …). */
    async executeActivityAction(activityId, command) {
        return this.request(`/activity/${activityId}/action`, 'POST', { command });
    }
    /** Send a one-off trial WhatsApp message to the recruiter's own number. */
    async sendWhatsAppTrial(phoneE164) {
        return this.request('/whatsapp/trial', 'POST', { phoneE164 });
    }
    // Jobs
    async getJobs(includeAll) {
        const queryParams = includeAll ? '?includeAll=true' : '';
        return this.request(`/jobs${queryParams}`, 'GET');
    }
    // Enhanced job creation with different input sources
    async createJob(job) {
        const request = {
            sourceType: 'manual',
            payload: job
        };
        return this.request('/job', 'POST', request);
    }
    // Create job from URL
    async createJobFromUrl(url) {
        const request = {
            sourceType: 'url',
            payload: url
        };
        return this.request('/job', 'POST', request);
    }
    // Create job from text
    async createJobFromText(text) {
        const request = {
            sourceType: 'text',
            payload: text
        };
        return this.request('/job', 'POST', request);
    }
    // Parse job description to extract structured data (using AI)
    async parseJobDescription(jobDescriptionText) {
        return this.request('/api/jobs/parse-description', 'POST', { jobDescriptionText });
    }
    // AI job optimization flow
    async getJobOptimizationQuestions(jobId) {
        return this.request(`/jobs/${jobId}/optimization-questions`, 'GET');
    }
    async submitJobOptimizationAnswers(answers) {
        return this.request('/jobs/optimize-scoring', 'POST', answers);
    }
    // Job scoring optimization (enhanced)
    async optimizeJobScoring(jobData) {
        return this.request('/jobs/optimize-scoring', 'POST', jobData);
    }
    async updateJob(jobId, job) {
        return this.request(`/job/${jobId}`, 'PUT', job);
    }
    async deleteJob(jobId) {
        return this.request(`/job/${jobId}`, 'DELETE');
    }
    async publishJob(jobId, publish) {
        return this.request(`/job/${jobId}/publish`, 'POST', { jobId, publish });
    }
    // Candidates API
    async getCandidates(includeAll) {
        const queryParams = includeAll ? '?includeAll=true' : '';
        return this.request(`/candidates${queryParams}`, 'GET');
    }
    async getCandidate(candidateId) {
        return this.request(`/candidates/${candidateId}`, 'GET');
    }
    async updateScheduledMessage(messageId, update) {
        return this.request(`/scheduledMessages/${messageId}`, 'PATCH', update);
    }
    /** Admin: edit/reschedule/cancel any scheduled message (cross-org). */
    async adminUpdateScheduledMessage(messageId, update) {
        return this.request(`/admin/scheduledMessages/${messageId}`, 'PATCH', update);
    }
    /** Admin: force-dispatch a Pending message immediately. */
    async adminSendScheduledMessageNow(messageId) {
        return this.request(`/admin/scheduledMessages/${messageId}/sendNow`, 'POST');
    }
    /** Admin: hard-delete a scheduled message. */
    async adminDeleteScheduledMessage(messageId) {
        return this.request(`/admin/scheduledMessages/${messageId}`, 'DELETE');
    }
    async markInterviewComplete(candidateId, request) {
        return this.request(`/candidates/${candidateId}/markInterviewComplete`, 'POST', request);
    }
    async sendDecisionMessage(candidateId, request) {
        return this.request(`/candidates/${candidateId}/sendDecisionMessage`, 'POST', request);
    }
    async previewDecisionMessage(candidateId, request) {
        return this.request(`/candidates/${candidateId}/previewDecisionMessage`, 'POST', request);
    }
    async updateWorkspaceAntiGhostingConfig(request) {
        return this.request('/workspace/antiGhostingConfig', 'PUT', request);
    }
    async updateJobAntiGhostingConfig(jobId, request) {
        return this.request(`/jobs/${jobId}/antiGhostingConfig`, 'PUT', request);
    }
    // ─── Per-candidate reminders (Phase B) ──────────────────────────────────
    async addCandidateReminder(candidateId, jobId, request) {
        return this.request(`/candidates/${candidateId}/applications/${jobId}/reminders`, 'POST', request);
    }
    async updateCandidateReminder(candidateId, jobId, reminderId, request) {
        return this.request(`/candidates/${candidateId}/applications/${jobId}/reminders/${reminderId}`, 'PUT', request);
    }
    async deleteCandidateReminder(candidateId, jobId, reminderId) {
        return this.request(`/candidates/${candidateId}/applications/${jobId}/reminders/${reminderId}`, 'DELETE');
    }
    // ─── No-response follow-ups ─────────────────────────────────────────────
    async sendResponseFollowUpNow(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/applications/${jobId}/response-follow-ups/send-now`, 'POST', {});
    }
    async skipResponseFollowUp(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/applications/${jobId}/response-follow-ups/skip`, 'POST', {});
    }
    async stopResponseFollowUps(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/applications/${jobId}/response-follow-ups/stop`, 'POST', {});
    }
    // ─── Candidate communications controls ──────────────────────────────────
    async retryApplicationAcknowledgement(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/applications/${jobId}/application-acknowledgement/retry`, 'POST', {});
    }
    async setCandidateAutomation(candidateId, command) {
        return this.request(`/candidates/${candidateId}/automation/${command}`, 'POST', {});
    }
    async updateJobQualificationWorkflow(jobId, request) {
        return this.request(`/jobs/${jobId}/qualification-workflow`, 'PUT', request);
    }
    async updateJobApplicationAcknowledgement(jobId, request) {
        return this.request(`/jobs/${jobId}/application-acknowledgement`, 'PUT', request);
    }
    async updateJobNoResponseFollowUps(jobId, request) {
        return this.request(`/jobs/${jobId}/no-response-follow-ups`, 'PUT', request);
    }
    async getOrgCommunicationsDefaults() {
        return this.request('/org/communications-defaults', 'GET');
    }
    /** The workspace's communication report counters. */
    async getCommMetrics() {
        return this.request('/candidate-communications/metrics', 'GET');
    }
    // ── Meta Lead Ads integration ─────────────────────────────────────────
    async getMetaLeadAds() {
        return this.request('/integrations/meta', 'GET');
    }
    /** The Meta sign-in page to send the browser to. */
    async connectMetaLeadAds() {
        return this.request('/integrations/meta/connect', 'POST', {});
    }
    async listMetaLeadForms(pageId) {
        return this.request(`/integrations/meta/pages/${encodeURIComponent(pageId)}/forms`, 'GET');
    }
    async getMetaLeadForm(pageId, formId) {
        return this.request(`/integrations/meta/pages/${encodeURIComponent(pageId)}/forms/${encodeURIComponent(formId)}`, 'GET');
    }
    async saveMetaLeadFormMapping(pageId, formId, request) {
        return this.request(`/integrations/meta/pages/${encodeURIComponent(pageId)}/forms/${encodeURIComponent(formId)}`, 'PUT', request);
    }
    async deleteMetaLeadFormMapping(pageId, formId) {
        return this.request(`/integrations/meta/pages/${encodeURIComponent(pageId)}/forms/${encodeURIComponent(formId)}`, 'DELETE');
    }
    async retryMetaLead(leadId) {
        return this.request(`/integrations/meta/leads/${encodeURIComponent(leadId)}/retry`, 'POST', {});
    }
    async disconnectMetaLeadAds() {
        return this.request('/integrations/meta/disconnect', 'POST', {});
    }
    /** Every workspace's communication counters for the last `days` days (admin). */
    async getAdminCommMetrics(days) {
        return this.request(`/admin/comm-metrics?days=${days}`, 'GET');
    }
    /** A page of recorded communication outcomes, newest first (admin). */
    async getAdminCommEvents(filter) {
        return this.request('/admin/comm-events', 'POST', filter);
    }
    async createCandidateUnified(request) {
        return this.request('/candidates/create', 'POST', request);
    }
    /**
     * Create a candidate from a resume file. Converts the file to base64 and sends
     * to the unified /candidates/create endpoint which handles parse+create+score.
     */
    async createCandidateFromResume(file, jobId, candidateData) {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        let binaryString = '';
        const chunkSize = 8192;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.slice(i, i + chunkSize);
            binaryString += String.fromCharCode.apply(null, Array.from(chunk));
        }
        const resumeBase64 = btoa(binaryString);
        return this.request('/candidates/create', 'POST', {
            resumeBase64,
            resumeFileName: file.name,
            jobId,
            candidateData,
        });
    }
    // Candidate Qualification Questions (new API)
    async startCandidateQuestions(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/startQuestions`, 'POST', { jobId });
    }
    async sendMessageToCandidate(candidateId, jobId, transport) {
        return this.request(`/candidates/${candidateId}/sendMessage`, 'POST', { jobId, transport });
    }
    async getCandidateQuestions(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/questions?jobId=${encodeURIComponent(jobId)}`, 'GET');
    }
    async respondToQuestion(candidateId, questionId, response) {
        return this.request(`/candidates/${candidateId}/questions/${questionId}/respond`, 'POST', { response });
    }
    // Candidate-Job Association API
    async associateCandidateWithJob(request) {
        return this.request('/candidates/associate-job', 'POST', request);
    }
    async removeCandidateFromJob(request) {
        return this.request('/candidates/remove-job', 'POST', request);
    }
    // Talent-pool rediscovery (Phase 0 sourcing)
    async getTalentPoolMatches(jobId, request) {
        return this.request(`/job/${jobId}/talent-pool`, 'POST', request);
    }
    async updateCandidateJobData(request) {
        return this.request('/candidates/update-job-data', 'POST', request);
    }
    // Messages
    async sendMessage(chatId, text) {
        return this.request('/sendMessage', 'POST', { chatId, text });
    }
    /** A page of a conversation: the newest, or (with `before`, a page's `earlierCursor`) the one before. */
    async getMessages(chatId, before) {
        const query = before === null ? { chatId } : { chatId, before: String(before) };
        return this.request('/messages', 'GET', query);
    }
    async getAllChats(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/chats${qs}`, 'GET');
    }
    // Interview
    async inviteToInterview(candidateId) {
        return this.request(`/candidates/${candidateId}/inviteToInterview`, 'POST');
    }
    // Candidate Interview (new API)
    async inviteCandidateToInterview(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/inviteToInterview`, 'POST', { jobId });
    }
    async batchSendMessages(req) {
        return this.request('/candidates/batch/send-message', 'POST', req);
    }
    async grantCandidateConsent(req) {
        return this.request('/candidates/consent', 'POST', req);
    }
    async batchInviteToInterview(req) {
        return this.request('/candidates/batch/invite-to-interview', 'POST', req);
    }
    async cancelInterview(eventId, reason) {
        return this.request(`/interviews/${eventId}/cancel`, 'POST', { eventId, reason });
    }
    async requestReschedule(req) {
        return this.request('/candidate/request-reschedule', 'POST', req);
    }
    async rescheduleInterview(req) {
        return this.request('/candidate/reschedule-interview', 'POST', req);
    }
    async updateProfile(data) {
        return this.request('/profile', 'PUT', data);
    }
    // Data deletion (GDPR/CCPA compliance)
    async getDataSummary() {
        return this.request('/user/data-summary', 'GET');
    }
    async deleteUserData(data) {
        return this.request('/user/delete-data', 'DELETE', data);
    }
    // User management
    async getAllUsers(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/users${qs}`, 'GET');
    }
    async getAllOrgs(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/admin/orgs${qs}`, 'GET');
    }
    async adminUpdateSubscription(orgId, patch) {
        return this.request(`/admin/orgs/${encodeURIComponent(orgId)}/subscription`, 'PATCH', patch);
    }
    async adminMoveUser(userId, body) {
        return this.request(`/admin/users/${encodeURIComponent(userId)}/move`, 'POST', body);
    }
    async adminRemoveVerifiedPhone(orgId, userId) {
        return this.request(`/admin/orgs/${encodeURIComponent(orgId)}/verified-phones/${encodeURIComponent(userId)}`, 'DELETE');
    }
    async adminUnlinkUserPhoneProvider(userId) {
        return this.request(`/admin/users/${encodeURIComponent(userId)}/phone-provider`, 'DELETE');
    }
    async adminGetPhoneVerificationStats(windowMs) {
        const qs = windowMs ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/admin/telemetry/phone-verification${qs}`, 'GET');
    }
    async adminGetTwilioStatus(limit) {
        const qs = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
        return this.request(`/admin/twilio/status${qs}`, 'GET');
    }
    /** Admin: list every WhatsApp template in the Twilio account (with live approval status). */
    async adminListWhatsAppTemplates(forceRefresh = false) {
        const qs = forceRefresh ? '?refresh=1' : '';
        return this.request(`/admin/whatsapp/templates${qs}`, 'GET');
    }
    /** Admin: send a WhatsApp template to any number for verification. */
    async adminSendWhatsAppTemplate(contentSid, payload) {
        return this.request(`/admin/whatsapp/templates/${encodeURIComponent(contentSid)}/test`, 'POST', payload);
    }
    async adminGetUserSnapshot(userId) {
        return this.request(`/admin/users/${encodeURIComponent(userId)}/snapshot`, 'GET');
    }
    async createAllowedUser(userData) {
        return this.request('/allowed-users', 'POST', userData);
    }
    async updateUser(userId, userData) {
        return this.request(`/users/${userId}`, 'PUT', userData);
    }
    async deleteUser(userId) {
        return this.request(`/users/${userId}`, 'DELETE');
    }
    // Allowed users management
    async getAllowedUsers(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/allowed-users${qs}`, 'GET');
    }
    async removeAllowedUser(email) {
        return this.request(`/allowed-users/${encodeURIComponent(email)}`, 'DELETE');
    }
    // ── Organization management ──
    async getOrg() {
        return this.request('/org', 'GET');
    }
    async updateOrg(updates) {
        return this.request('/org', 'PUT', updates);
    }
    async inviteMember(email, role) {
        return this.request('/org/invite', 'POST', { email, role });
    }
    async cancelInvitation(inviteId) {
        return this.request(`/org/invite/${inviteId}`, 'DELETE');
    }
    async getOrgInvitations() {
        return this.request('/org/invitations', 'GET');
    }
    async acceptInvitation(inviteId) {
        return this.request(`/org/invite/${inviteId}/accept`, 'POST');
    }
    async declineInvitation(inviteId) {
        return this.request(`/org/invite/${inviteId}/decline`, 'POST');
    }
    async removeMember(userId) {
        return this.request(`/org/member/${userId}`, 'DELETE');
    }
    async transferOwnership(targetUserId) {
        return this.request('/org/transfer', 'POST', { targetUserId });
    }
    async leaveOrg() {
        return this.request('/org/leave', 'POST');
    }
    // ── Stripe billing ──
    async createCheckoutSession(body) {
        return this.request('/stripe/create-checkout-session', 'POST', body);
    }
    async createPortalSession(body) {
        return this.request('/stripe/create-portal-session', 'POST', body);
    }
    // ── Admin Stripe / Coupons ──
    async adminListCoupons() {
        return this.request('/admin/stripe/coupons', 'GET');
    }
    async adminCreateCoupon(body) {
        return this.request('/admin/stripe/coupons', 'POST', body);
    }
    async adminDeleteCoupon(id) {
        return this.request(`/admin/stripe/coupons/${encodeURIComponent(id)}`, 'DELETE');
    }
    async adminListPromotionCodes() {
        return this.request('/admin/stripe/promotion-codes', 'GET');
    }
    async adminCreatePromotionCode(body) {
        return this.request('/admin/stripe/promotion-codes', 'POST', body);
    }
    async adminUpdatePromotionCode(id, active) {
        return this.request(`/admin/stripe/promotion-codes/${encodeURIComponent(id)}`, 'PATCH', { active });
    }
    async adminListPayments(limit = 50) {
        return this.request(`/admin/stripe/payments?limit=${limit}`, 'GET');
    }
    async adminRefundPayment(body) {
        return this.request('/admin/stripe/refund', 'POST', body);
    }
    // ── Phone verification (Free-tier SMS gating) ──
    async startPhoneVerification(phoneE164) {
        return this.request('/me/phone/start-verification', 'POST', { phoneE164 });
    }
    async confirmPhoneVerification(phoneE164, firebasePhoneIdToken) {
        return this.request('/me/phone/confirm-verification', 'POST', { phoneE164, firebasePhoneIdToken });
    }
    async removeVerifiedPhone() {
        return this.request('/me/phone', 'DELETE');
    }
    // ── Notification subscriptions ──
    /** Keep this tab's notification subscription from expiring during a long session. */
    async notificationsHeartbeat(tabId) {
        const body = { tabId };
        return this.request('/notifications/heartbeat', 'POST', body);
    }
    async unsubscribeNotifications(tabId) {
        const body = { tabId, token: null };
        await this.request('/notifications/unsubscribe', 'POST', body);
    }
    /**
     * Fire-and-forget unsubscribe via sendBeacon (survives page unload).
     * Token is sent in the body since sendBeacon cannot set headers.
     */
    unsubscribeNotificationsSync(tabId) {
        const url = `${this.baseUrl}/notifications/unsubscribe`;
        const body = { tabId, token: this.token };
        const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
    }
    // Waitlist management
    async getWaitlist(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/waitlist${qs}`, 'GET');
    }
    // Audit-quiz lead management
    async getAuditLeads(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/audit-leads${qs}`, 'GET');
    }
    async getQuizLeads(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/quiz-leads${qs}`, 'GET');
    }
    // User management - comprehensive approach
    async updateUserPrivileged(userId, updates, reason) {
        return this.request(`/users/${userId}`, 'PUT', { updates, reason });
    }
    async getUserDetails(userId) {
        return this.request(`/users/${userId}/details`, 'GET');
    }
    async deleteCandidates(candidateIds) {
        return this.request('/candidates/delete-bulk', 'POST', { candidateIds });
    }
    async resolveCandidateConflict(candidateId, body) {
        return this.request(`/candidates/${candidateId}/resolve-conflict`, 'POST', body);
    }
    async adminGetAllCandidates(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/admin/candidates${qs}`, 'GET');
    }
    async adminDeleteCandidates(candidateIds) {
        return this.request('/admin/candidates/delete-bulk', 'POST', { candidateIds });
    }
    async scoreCandidate(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/score`, 'POST', { jobId });
    }
    async updateCandidateScore(candidateId, jobId, newScore, reason, newRecommendation) {
        return this.request(`/candidates/${candidateId}/score`, 'PUT', { candidateId, jobId, newScore, reason, newRecommendation });
    }
    async updateCandidateHiringStatus(candidateId, jobId, status, remark) {
        return this.request(`/candidates/${candidateId}/hiring-status`, 'PUT', { candidateId, jobId, status, remark });
    }
    async setCandidateState(candidateId, jobId, state) {
        return this.request(`/candidates/${candidateId}/setState`, 'POST', { jobId, state });
    }
    async acceptCandidate(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/accept`, 'POST', { jobId });
    }
    async rejectCandidate(candidateId, jobId, reason) {
        return this.request(`/candidates/${candidateId}/reject`, 'POST', { jobId, reason });
    }
    async restoreCandidate(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/restore`, 'POST', { jobId });
    }
    async sendToOffer(candidateId, jobId) {
        return this.request(`/candidates/${candidateId}/send-to-offer`, 'POST', { jobId });
    }
    // =====================================
    // Scoring Settings
    // =====================================
    // =====================================
    // PDF Upload Methods
    // =====================================
    // Upload raw resume buffer (PDF, DOC, DOCX) to unified parse endpoint
    async parseResumeFile(fileBuffer, fileName, opts) {
        const lowerName = fileName.toLowerCase();
        const isDocx = lowerName.endsWith('.docx');
        const isDoc = lowerName.endsWith('.doc');
        let contentType;
        if (isDocx) {
            contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        }
        else if (isDoc) {
            contentType = 'application/msword';
        }
        else {
            contentType = 'application/pdf';
        }
        const headers = {
            'Content-Type': contentType,
            'X-File-Name': encodeURIComponent(fileName || 'uploaded')
        };
        return makeRequest(this.apiUrl, this.authToken, '/candidates/parse', 'POST', undefined, { headers, rawBody: fileBuffer, signal: opts?.signal });
    }
    async parseCandidatesFromPdfsBatch(filesData) {
        const headers = {
            'Content-Type': 'application/json',
        };
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        const requestBody = { files: filesData };
        const response = await fetch(`${this.apiUrl}/candidates/parse-batch`, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Batch PDF parsing failed (${response.status}): ${errorText}`);
        }
        return response.json();
    }
    async submitFeedback(feedbackData) {
        return this.request('/feedback', 'POST', feedbackData);
    }
    async getFeedback(windowMs) {
        const qs = windowMs !== undefined ? `?windowMs=${encodeURIComponent(String(windowMs))}` : '';
        return this.request(`/feedback${qs}`, 'GET');
    }
    // Pending session conversion (value-first onboarding)
    async convertPendingSession(sessionId) {
        return this.request('/pending-session/convert', 'POST', { sessionId });
    }
    // -------------------- Admin Migration Endpoints --------------------
    async getMigrationStatus() {
        return this.request('/admin/migrations/jobs', 'GET');
    }
    async runMigration(jobIds) {
        return this.request('/admin/migrations/jobs', 'POST', { jobIds });
    }
}
async function makeRequest(apiUrl, idToken, path, method = 'GET', bodyObj, options) {
    let url = `${apiUrl}${path}`;
    const headers = { ...(options?.headers || {}) };
    if (idToken) {
        headers['Authorization'] = `Bearer ${idToken}`;
    }
    // Only set JSON content-type if not provided and we're sending JSON (no raw body)
    if (!('Content-Type' in headers) && method !== 'GET' && !options?.rawBody) {
        headers['Content-Type'] = 'application/json';
    }
    if (method === 'GET' && bodyObj) {
        const sep = url.includes('?') ? '&' : '?';
        url += `${sep}${new URLSearchParams(bodyObj).toString()}`;
    }
    let body;
    if (options?.rawBody) {
        body = options.rawBody; // Raw binary upload
    }
    else if (method !== 'GET') {
        body = JSON.stringify(bodyObj || {});
    }
    const init = { method, headers, body, signal: options?.signal };
    const res = await fetch(url, init);
    if (!res.ok) {
        const errText = await res.text();
        // Try to parse as ErrorResponse and throw ApiError
        try {
            const errorResponse = JSON.parse(errText);
            if (errorResponse.code && errorResponse.error) {
                throw ApiError.fromResponse(errorResponse);
            }
        }
        catch (parseError) {
            // If it's already an ApiError, rethrow it
            if (ApiError.isApiError(parseError)) {
                throw parseError;
            }
            // Otherwise throw generic error with the raw text
        }
        throw new Error(`${method} ${url} failed: ${errText}`);
    }
    // Try parse JSON; fall back to text if empty
    const text = await res.text();
    if (!text)
        return {};
    try {
        return JSON.parse(text);
    }
    catch {
        return { raw: text };
    }
}
